import React, { useEffect, useState, useRef } from 'react';
import { Box } from '@mui/material';
import { useGetStorehouses } from 'api/counterparties';
import { Storehouse } from 'api/counterparties/counterparties.types';
import { AutocompletePaginationAsync } from 'components/Inputs';
import { CUSTOMS_CLEARANCE_STATUSES } from '../../TransportCloudTable.consts';

interface EditableCustomsClearanceInputProps {
  value: any;
  onChange: (value: any) => void;
  onSave: (value: any) => void;
  onCancel: () => void;
  rowData?: any;
}

interface CustomsClearanceValue {
  status: string | null;
  placeId: number | null;
  place?: any;
}

type StatusOption = 'DONE' | 'IN_PROGRESS' | 'NONE';

const parseInitialValue = (val: any, rowData?: any): CustomsClearanceValue => {
  if (typeof val === 'object' && val !== null) {
    return {
      status: val.status || null,
      placeId: val.placeId || null,
      place: val.place || null,
    };
  }

  if (typeof val === 'string') {
    const lowerValue = val.toLowerCase().trim();
    let status: string | null = null;

    if (
      lowerValue.includes('done') ||
      lowerValue.includes('odpraw') ||
      lowerValue.includes('zakład')
    ) {
      status = 'DONE';
    } else if (
      lowerValue.includes('progress') ||
      lowerValue.includes('tranzyt') ||
      lowerValue.includes('port') ||
      lowerValue.includes('porcie')
    ) {
      status = 'IN_PROGRESS';
    } else if (lowerValue.includes('none') || lowerValue.includes('brak')) {
      status = 'NONE';
    }

    const placeId = rowData?.customsClearancePlaceId || null;
    const placeCode = rowData?.customsClearancePlaceCode || null;

    return {
      status,
      placeId,
      place: placeId && placeCode ? { id: placeId, code: placeCode } : null,
    };
  }

  return { status: null, placeId: null, place: null };
};

export const EditableCustomsClearanceInput: React.FC<
  EditableCustomsClearanceInputProps
> = ({ value: initialValue, onChange, onSave, onCancel, rowData }) => {
  const apiHook = useGetStorehouses();
  const { data, loading } = apiHook;
  const getter = apiHook.getStorehouses;
  const containerRef = useRef<HTMLDivElement>(null);
  const [allOptions, setAllOptions] = useState<any[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  const customerId = rowData?.customerId;
  const tradeType = rowData?.tradeType || 'IMPORT';

  const getStatusLabel = (status: StatusOption): string => {
    const labels =
      tradeType === 'EXPORT'
        ? {
            DONE: CUSTOMS_CLEARANCE_STATUSES.export.done,
            IN_PROGRESS: CUSTOMS_CLEARANCE_STATUSES.export.inProgress,
            NONE: CUSTOMS_CLEARANCE_STATUSES.none,
          }
        : {
            DONE: CUSTOMS_CLEARANCE_STATUSES.import.done,
            IN_PROGRESS: CUSTOMS_CLEARANCE_STATUSES.import.inProgress,
            NONE: CUSTOMS_CLEARANCE_STATUSES.none,
          };

    return labels[status as keyof typeof labels] || '';
  };

  const [localValue, setLocalValue] = useState<CustomsClearanceValue>(() =>
    parseInitialValue(initialValue, rowData),
  );

  useEffect(() => {
    if (!customerId) return;

    if (typeof getter === 'function') {
      const getterWithDep = getter(customerId);
      if (typeof getterWithDep === 'function') {
        getterWithDep({ resultSize: 1000, pageNumber: 1 });
      }
    }
  }, [getter, customerId]);

  useEffect(() => {
    if (data?.length) {
      const processedOptions = data
        .map((storehouse: Storehouse) => ({
          id: storehouse.id,
          code: storehouse.code,
          name: storehouse.name,
          address: storehouse.address,
          description: storehouse.description,
          enabled: storehouse.enabled,
        }))
        .filter((option: any) => option.enabled);

      setAllOptions(processedOptions);
      setFilteredOptions(processedOptions);
    }
  }, [data]);

  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(allOptions);
      return;
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    const filtered = allOptions.filter((option) => {
      const codeMatch = option.code?.toLowerCase()?.includes(lowerSearchTerm);
      const nameMatch = option.name?.toLowerCase()?.includes(lowerSearchTerm);
      const cityMatch = option.address?.city
        ?.toLowerCase()
        ?.includes(lowerSearchTerm);
      const lineOneMatch = option.address?.lineOne
        ?.toLowerCase()
        ?.includes(lowerSearchTerm);
      const lineTwoMatch = option.address?.lineTwo
        ?.toLowerCase()
        ?.includes(lowerSearchTerm);

      return (
        codeMatch || nameMatch || cityMatch || lineOneMatch || lineTwoMatch
      );
    });
    setFilteredOptions(filtered);
  }, [searchTerm, allOptions]);

  const formatValueForSave = (customsValue: CustomsClearanceValue) => ({
    status: customsValue.status || null,
    placeId: customsValue.placeId || null,
  });

  const handleStorehouseChange = (_: any, newStorehouse: any | null) => {
    const newValue = {
      ...localValue,
      placeId: newStorehouse?.id || null,
      place: newStorehouse,
    };
    setLocalValue(newValue);
    onChange(formatValueForSave(newValue));
  };

  const handleStorehouseSearchChange = (searchText: string) => {
    setSearchTerm(searchText);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onCancel();
    } else if (event.key === 'Enter') {
      onSave(formatValueForSave(localValue));
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      const currentFocus = document.activeElement;

      if (
        !containerRef.current ||
        !containerRef.current.contains(currentFocus)
      ) {
        onSave(formatValueForSave(localValue));
      }
    }, 200);
  };

  const isStorehouseEnabled =
    (tradeType === 'IMPORT' && localValue.status === 'IN_PROGRESS') ||
    (tradeType === 'EXPORT' && localValue.status === 'DONE');

  return (
    <Box
      ref={containerRef}
      display='flex'
      gap={0.5}
      alignItems='center'
      onKeyDown={handleKeyDown}
      sx={{
        width: '320px',
        height: 'auto',
        maxHeight: '25px',
        '& .MuiOutlinedInput-root': {
          height: '24px',
          minHeight: '24px',
          padding: '0px 8px',
          fontSize: '0.7rem',
        },
        '& .MuiInputBase-input': {
          padding: '0px',
          height: '24px',
          lineHeight: '1.5em',
          fontSize: '0.7rem',
        },
        '& .MuiAutocomplete-endAdornment': { top: 'calc(50% - 10px)' },
        '& .MuiAutocomplete-clearIndicator': {
          padding: '1px',
          '& .MuiSvgIcon-root': { fontSize: '12px' },
        },
        '& .MuiAutocomplete-popupIndicator': {
          padding: '1px',
          '& .MuiSvgIcon-root': { fontSize: '12px' },
        },
        '& .MuiCircularProgress-root': {
          width: '12px !important',
          height: '12px !important',
        },
      }}
      data-customs-clearance-container
    >
      <AutocompletePaginationAsync
        padding='dense'
        disableClearable={true}
        width={110}
        value={
          localValue.status
            ? {
                value: localValue.status,
                label: getStatusLabel(localValue.status as StatusOption),
              }
            : null
        }
        loading={false}
        options={[
          { value: 'DONE', label: getStatusLabel('DONE') },
          { value: 'IN_PROGRESS', label: getStatusLabel('IN_PROGRESS') },
          { value: 'NONE', label: getStatusLabel('NONE') },
        ]}
        isOptionEqualToValue={(option, selectedValue) => {
          if (!selectedValue) return false;
          return option?.value === selectedValue.value;
        }}
        getOptionLabel={(option) => {
          if (!option) return '';
          return option.label || '';
        }}
        handleSearchChange={() => {}}
        {...({
          componentsProps: {
            paper: {
              sx: {
                '& .MuiAutocomplete-option': {
                  fontSize: '0.7rem',
                  padding: '2px 8px',
                  minHeight: '28px',
                },
                '& .MuiAutocomplete-noOptions': {
                  fontSize: '0.7rem',
                  padding: '4px 8px',
                },
              },
            },
          },
        } as any)}
        onChange={(_, selectedStatus) => {
          const newStatus = selectedStatus?.value || null;
          const shouldKeepPlace =
            (tradeType === 'IMPORT' && newStatus === 'IN_PROGRESS') ||
            (tradeType === 'EXPORT' && newStatus === 'DONE');

          const newValue = {
            ...localValue,
            status: newStatus,
            placeId: shouldKeepPlace ? localValue.placeId : null,
            place: shouldKeepPlace ? localValue.place : null,
          };
          setLocalValue(newValue);
          onChange(formatValueForSave(newValue));
        }}
        onScrollBottom={() => {}}
        onBlur={handleBlur}
      />

      <Box
        sx={{
          width: '180px',
          minWidth: '180px',
        }}
      >
        <AutocompletePaginationAsync
          padding='dense'
          width={110}
          value={localValue.place}
          loading={loading}
          options={filteredOptions}
          disabled={!isStorehouseEnabled}
          isOptionEqualToValue={(option, selectedValue) => {
            if (!selectedValue) return false;
            if (typeof selectedValue === 'object' && 'id' in selectedValue) {
              return option?.id === selectedValue.id;
            }
            return false;
          }}
          getOptionLabel={(option) => {
            if (!option) return '';
            return option.code || '';
          }}
          handleSearchChange={handleStorehouseSearchChange}
          onChange={handleStorehouseChange}
          onScrollBottom={() => {}}
          onBlur={handleBlur}
          {...({
            componentsProps: {
              paper: {
                sx: {
                  '& .MuiAutocomplete-option': {
                    fontSize: '0.7rem',
                    padding: '2px 8px',
                    minHeight: '28px',
                  },
                  '& .MuiAutocomplete-noOptions': {
                    fontSize: '0.7rem',
                    padding: '4px 8px',
                  },
                },
              },
            },
          } as any)}
        />
      </Box>
    </Box>
  );
};
